import { validateAddressForGoogleMaps } from './addressValidation';

/**
 * EIS-979 Part 1 — shared, coords-FIRST navigation (adopted from the
 * clee/consistent-google-search-and-nav draft, reviewed + fixed + tested).
 *
 * Why coords-first: the old baseball-card / map-minicard nav built Google &
 * Apple URLs from the practitioner's ADDRESS (maps/search/?api=1&query=...),
 * which returns a BLANK Google result when the address is malformed (repro:
 * jmontague -> "Adam Breiner") even though valid coordinates exist. Routes nav
 * already did it right: maps/dir/?api=1&destination=<lat>,<lng>.
 *
 * Fallback strategy (see buildNavigationUrl):
 *   1. Google Places ID — FUTURE no-op (EIS-979 Part 3 will store PlacesID in
 *      the backend; nothing in the data carries one today, so this branch
 *      deliberately falls through to coordinates and must NOT build URLs yet)
 *   2. Coordinates — precise, immune to malformed addresses
 *   3. Address search — only when no usable coordinates exist
 *
 * Fixes made to the draft:
 *   - Google coords URL without an origin used maps/search/?api=1&query=<lat>,<lng>;
 *     now maps/dir/?api=1&destination=<lat>,<lng> (same pattern Routes uses, and
 *     Google/Waze/Apple all start navigation from the user's current location
 *     when no origin is given).
 *   - hasCoordinates() accepted garbage like "abc" (NaN); now requires finite numbers.
 *   - Waze address-only fallback silently did nothing (console.warn, no onError);
 *     Waze deep links support q=<address>, so use it.
 *   - Geolocation lookup is now OPT-IN (getCurrentLocation defaults to false):
 *     window.open fired from an async geolocation callback is outside the user
 *     gesture and gets popup-blocked (Safari/iOS especially), and destination-only
 *     URLs already navigate from the user's current position.
 *   - URL building extracted into pure, unit-testable functions.
 */

export interface LocationData {
  /** Google Places ID — future (EIS-979 Part 3). Never populated today. */
  placesId?: string;
  latitude?: string | number;
  longitude?: string | number;
  address?: string;
  name?: string;
}

export interface OriginCoords {
  latitude: number;
  longitude: number;
}

export interface NavigationOptions {
  /**
   * Opt-in: resolve the user's position first and pass it as the route origin.
   * Off by default — the async geolocation callback breaks out of the click
   * gesture, so popup blockers can eat the window.open, and every maps app
   * already defaults the origin to the user's current location.
   */
  getCurrentLocation?: boolean;
  onError?: (error: string) => void;
  onLoading?: (isLoading: boolean) => void;
}

export type NavigationApp = 'google' | 'waze' | 'apple';

/** True when the location carries usable, on-Earth, non-placeholder coordinates. */
export const hasValidCoordinates = (location: LocationData): boolean => {
  if (location.latitude === null || location.latitude === undefined || location.latitude === '') return false;
  if (location.longitude === null || location.longitude === undefined || location.longitude === '') return false;
  const lat = Number(location.latitude);
  const lng = Number(location.longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
  // Range-check: out-of-range values are corrupt data, not a real place — fall
  // back to address rather than emit a bogus destination=91,200 URL.
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return false;
  // Only the EXACT (0,0) pair (null island) is the missing-coords placeholder.
  // A location on the equator (0, lng) or prime meridian (lat, 0) is legitimate.
  if (lat === 0 && lng === 0) return false;
  return true;
};

/**
 * Pure URL builder implementing the fallback chain:
 * PlacesID (future no-op) -> coordinates -> address search -> null.
 */
export const buildNavigationUrl = (
  location: LocationData,
  app: NavigationApp,
  origin?: OriginCoords
): string | null => {
  // 1) Google Places ID — FUTURE (Part 3): the backend does not store PlacesID
  //    yet, so this branch is a deliberate no-op that falls through to coords.
  //    Do NOT build Places URLs here until the data actually carries one.

  // 2) Coordinates
  if (hasValidCoordinates(location)) {
    const dest = `${Number(location.latitude)},${Number(location.longitude)}`;
    const from = origin ? `${origin.latitude},${origin.longitude}` : null;
    switch (app) {
      case 'google':
        return from
          ? `https://www.google.com/maps/dir/?api=1&origin=${from}&destination=${dest}`
          : `https://www.google.com/maps/dir/?api=1&destination=${dest}`;
      case 'waze':
        return from
          ? `https://waze.com/ul?ll=${dest}&navigate=yes&from=${from}`
          : `https://waze.com/ul?ll=${dest}&navigate=yes`;
      case 'apple':
        return from
          ? `maps://?saddr=${from}&daddr=${dest}`
          : `maps://?daddr=${dest}`;
      default:
        return null;
    }
  }

  // 3) Address search
  const rawAddress = (location.address || '').trim();
  if (rawAddress) {
    // Best effort: strip name lines / line breaks when the validator can; fall
    // back to the raw address otherwise (a fuzzy search beats doing nothing).
    const { isValid, cleanedAddress } = validateAddressForGoogleMaps(rawAddress);
    const query = encodeURIComponent(isValid && cleanedAddress ? cleanedAddress : rawAddress);
    switch (app) {
      case 'google':
        return `https://www.google.com/maps/search/?api=1&query=${query}`;
      case 'waze':
        return `https://waze.com/ul?q=${query}&navigate=yes`;
      case 'apple':
        return `maps://?q=${query}`;
      default:
        return null;
    }
  }

  return null;
};

/**
 * Normalizes the shapes the app passes around into LocationData:
 *   - route start/end locations: { isFirstLocation|isLastLocation, address, latitude, longitude }
 *   - transient map locations (Google Places search pins / lead cards):
 *     { id: string, latLon: [lat, lng], formatted_address, name }
 *   - practitioners: default-address FK record (addresses.find(a => a.id === default_address)),
 *     falling back to the practitioner's flat latitude/longitude when no record matches.
 * Returns undefined when nothing navigable exists.
 */
export const resolveNavigationLocation = (subject: any): LocationData | undefined => {
  if (!subject) return undefined;

  if (subject.isFirstLocation || subject.isLastLocation) {
    return {
      address: subject.address ?? '',
      latitude: subject.latitude,
      longitude: subject.longitude,
      name: subject.name,
    };
  }

  // Transient map location (string id) — coordinates live in latLon
  if (typeof subject.id === 'string' || Array.isArray(subject.latLon)) {
    const latLon = Array.isArray(subject.latLon) ? subject.latLon : [];
    return {
      latitude: latLon[0],
      longitude: latLon[1],
      address: subject.formatted_address ?? subject.address ?? '',
      name: subject.name ?? subject.primary_name,
      placesId: undefined, // future (Part 3): subject.id IS the Places ID for search pins
    };
  }

  // Practitioner — the default-address FK record is the source of truth
  const defaultAddress = subject.addresses?.find?.(
    (a: any) => a.id === subject.default_address
  );
  const candidate: LocationData = defaultAddress
    ? {
        latitude: defaultAddress.latitude,
        longitude: defaultAddress.longitude,
        address: defaultAddress.address ?? '',
        name: subject.name ?? subject.primary_name,
      }
    : {
        latitude: subject.latitude,
        longitude: subject.longitude,
        address: subject.address ?? '',
        name: subject.name ?? subject.primary_name,
      };

  if (hasValidCoordinates(candidate) || (candidate.address || '').trim()) {
    return candidate;
  }
  return undefined;
};

/**
 * Smart navigation utility — one entry point for every Navigate button.
 */
export class NavigationService {
  /**
   * Navigate to a location using the specified app with smart fallback.
   * Synchronous by default so window.open stays inside the user gesture.
   */
  static navigateToLocation(
    location: LocationData,
    app: NavigationApp,
    options: NavigationOptions = {}
  ): void {
    const { getCurrentLocation = false, onError, onLoading } = options;

    if (!this.validateLocationData(location)) {
      const error = 'Invalid location data - no coordinates or address available';
      console.error(error, location);
      onError?.(error);
      return;
    }

    if (getCurrentLocation && hasValidCoordinates(location)) {
      // Opt-in only: the window.open below runs in an async callback, which
      // popup blockers may reject. Default flow never takes this path.
      onLoading?.(true);
      this.getCurrentPosition(
        (currentLat, currentLng) => {
          onLoading?.(false);
          this.openUrl(location, app, onError, { latitude: currentLat, longitude: currentLng });
        },
        (error) => {
          onLoading?.(false);
          console.warn('Could not get current location, navigating without origin:', error);
          this.openUrl(location, app, onError);
        }
      );
      return;
    }

    this.openUrl(location, app, onError);
  }

  private static openUrl(
    location: LocationData,
    app: NavigationApp,
    onError?: (error: string) => void,
    origin?: OriginCoords
  ): void {
    const url = buildNavigationUrl(location, app, origin);
    if (!url) {
      const error = `No valid navigation data available for ${app}`;
      console.error(error, location);
      onError?.(error);
      return;
    }
    window.open(url, '_blank');
  }

  /** Get current user location (used only when getCurrentLocation opts in). */
  private static getCurrentPosition(
    onSuccess: (lat: number, lng: number) => void,
    onError: (error: string) => void
  ): void {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          onSuccess(position.coords.latitude, position.coords.longitude);
        },
        (error) => {
          let errorMessage = 'Unknown geolocation error';
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'Location access denied by user';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'Location information unavailable';
              break;
            case error.TIMEOUT:
              errorMessage = 'Location request timed out';
              break;
            default:
              break;
          }
          onError(errorMessage);
        },
        {
          timeout: 10000,
          enableHighAccuracy: false,
        }
      );
    } else {
      onError('Geolocation not supported by this browser');
    }
  }

  /** Validate that location has usable data. */
  private static validateLocationData(location: LocationData): boolean {
    return !!(
      location.placesId ||
      hasValidCoordinates(location) ||
      (location.address || '').trim()
    );
  }

  /** Get the best available navigation method for a location. */
  static getNavigationMethod(
    location: LocationData
  ): 'places_id' | 'coordinates' | 'address' | 'unavailable' {
    // 'places_id' stays reachable for forward-compat, but navigation itself
    // treats it as a no-op until Part 3 lands.
    if (location.placesId) return 'places_id';
    if (hasValidCoordinates(location)) return 'coordinates';
    if ((location.address || '').trim()) return 'address';
    return 'unavailable';
  }

  /** Check if navigation app supports address-only navigation. */
  static supportsAddressNavigation(app: NavigationApp): boolean {
    return app === 'google' || app === 'waze' || app === 'apple';
  }
}
